import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	EventEmitter,
	Input,
	OnDestroy,
	OnInit,
	Output,
	ViewChild
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import * as Highcharts from 'highcharts';
import { Marker } from '@go-modules/interfaces/markers/marker';
import type { PlayerSync } from '@go-modules/interfaces/media/media';
import { FormatTimePipe } from '@go-modules/pipes/format-time/format-time.pipe';
import { ProcessedComment } from '@go-modules/services/comment-list/comment-list.service';
import { CommentListService } from '@go-modules/services/comment-list/comment-list.service';
import { Observable, Subject, takeUntil } from 'rxjs';
import { NgxFeatureFlagService } from '@go-modules/services/feature-flag/feature-flag.service';
import { providePartialHighcharts } from 'highcharts-angular';

interface SeriesData extends Highcharts.PointOptionsObject {
	custom: {
		actualTime: number;
		commenter: string;
		commentText?: string;
		markerName?: string;
	}
}


@Component({
	selector: 'feedback-graph',
	templateUrl: './feedback-graph.component.html',
	styleUrls: ['./feedback-graph.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [
		providePartialHighcharts({
			modules: () => {
				return [
					import('highcharts/esm/modules/accessibility')
				];
			}
		})
	],
	standalone: false
})
export class FeedbackGraphComponent implements OnInit, OnDestroy {
	@Output() public onExpanded = new EventEmitter();

	@Input() public isExpanded: boolean;
	@Input() public duration: number;
	@Input() public tagsList: Marker[];
	@Input() public playerSync: PlayerSync;

	public comments$: Observable<ProcessedComment[]>;
	public Highcharts: typeof Highcharts = Highcharts;
	public chartOptions: Highcharts.Options;
	public markersWithCount: Marker[];
	public commentSeriesData: SeriesData[] = [];
	public aiMarkerSeriesData: SeriesData[] = [];
	public markerSeriesData: SeriesData[] = [];
	public feedbackSummary: string;
	private destroyed$ = new Subject();

	@ViewChild('feedbackChart') public chartRef;

	constructor (
		private translate: TranslateService,
		private commentListService: CommentListService,
		public featureFlagService: NgxFeatureFlagService,
		private cdr: ChangeDetectorRef
	) {}

	public ngOnInit () {
		this.setChartOptions();
	}

	public ngOnDestroy () {
		this.destroyed$.next(true);
		this.destroyed$.complete();
	}

	public setChartOptions () {
		// to handle both short and long videos and make the chart still look good
		// and not just be linear because of small differences in timestamp milliseconds
		// we need to group the comments into 10-25 groups based on the length of the video
		// so we can stack them up if they are in a similar range
		const minGroups = 10;
		const maxGroups = 25;
		const numGroups = Math.min(maxGroups, Math.max(minGroups, Math.ceil(this.duration / 60000)));
		const intervalDuration = this.duration / numGroups;

		this.markersWithCount = JSON.parse(JSON.stringify(this.tagsList));
		this.comments$ = this.commentListService.comments$;
		this.comments$.pipe(takeUntil(this.destroyed$)).subscribe((comments) => {
			this.commentSeriesData = [];
			this.markerSeriesData = [];
			comments.forEach((comment) => {
				// dont care about endnotes
				if (comment.time === null) {
					return;
				}

				if (comment.resource && comment.resource.length) {
					let isOnlyMarker = false;
					comment.resource.forEach((resource) => {
						if (resource.resource_type === 'Tag') {
							const markerItem = {
								x: Math.floor(Number(comment.time) / intervalDuration) * intervalDuration,
								y: 1,
								custom: {
									actualTime: Number(comment.time),
									markerName: resource.item.tag_name,
									commenter: comment.first_name + ' ' + comment.last_name
								},
								marker: {
									enabled: true,
									fillColor: comment.was_ai_generated ? '#ffffff' : `#${resource.item.tag_color}`,
									lineColor: `#${resource.item.tag_color}`,
									lineWidth: 1
								}
							};

							if (comment.was_ai_generated && this.featureFlagService.isAvailable('AI_CONFIG')) {
								const color = `#${resource.item.category?.color ?? resource.item.tag_color}`;
								markerItem.marker.fillColor = color;
								markerItem.marker.lineColor = color;
								this.aiMarkerSeriesData.push(markerItem);
							} else {
								this.markerSeriesData.push(markerItem);
							}

							const matchingMarker = this.markersWithCount
								.find((m) => m.tag_name === resource.item.tag_name);
							if (matchingMarker) {
								matchingMarker.count = (matchingMarker.count || 0) + 1;
							} else {
								resource.item.count = 1;
								resource.item.is_ai_marker = comment.was_ai_generated;
								this.markersWithCount.push(resource.item);
							}

							if (!comment.text) {
								isOnlyMarker = true;
							}
						}
					});
					if (!isOnlyMarker) {
						this.commentSeriesData.push({
							x: Math.floor(Number(comment.time) / intervalDuration) * intervalDuration,
							y: 1,
							custom: {
								actualTime: Number(comment.time),
								commentText: comment.text,
								commenter: comment.first_name + ' ' + comment.last_name
							}
						});
					}
				} else {
					this.commentSeriesData.push({
						x: Math.floor(Number(comment.time) / intervalDuration) * intervalDuration,
						y: 1,
						custom: {
							actualTime: Number(comment.time),
							commentText: comment.text,
							commenter: comment.first_name + ' ' + comment.last_name
						}
					});
				}
			});
			this.feedbackSummary = this.translate.instant('session-analytics_feedback-graph-summary', {
				markers: this.markerSeriesData.length,
				comments: this.commentSeriesData.length
			});

			this.commentSeriesData.sort((a,b) => {
				return a.custom.actualTime - b.custom.actualTime;
			});
			this.markerSeriesData.sort((a,b) => {
				return a.custom.actualTime - b.custom.actualTime;
			});
			this.aiMarkerSeriesData.sort((a,b) => {
				return a.custom.actualTime - b.custom.actualTime;
			});

			this.markersWithCount.map((marker) => marker.is_ai_marker = marker.is_ai_marker ?? false);
			this.markersWithCount = this.markersWithCount
				.filter((marker) => marker.count > 0)
				.sort((a: any, b: any) => b.is_ai_marker - a.is_ai_marker);

			this.chartOptions = {
				lang: {
					accessibility: {
						svgContainerLabel:
							`${this.translate.instant('session-analytics_feedback-graph')} - ${
								this.translate.instant('session-analytics_interactive-chart')
							}`
					}
				},
				chart: {
					type: 'scatter',
					height: 200,
					plotBackgroundColor: '#F7F7F7'
				},
				title: {
					text: ''
				},
				xAxis: {
					minPadding: 0.025,
					maxPadding: 0.025,
					startOnTick: false,
					lineWidth: 0,
					tickWidth: 0,
					labels: {
						overflow: 'justify',
						// eslint-disable-next-line object-shorthand
						formatter: function () {
							const timePipe = new FormatTimePipe();
							return timePipe.transform(Number(this.value));
						}
					},
					softMin: 0,
					max: this.duration,
					minTickInterval: intervalDuration
				},
				yAxis: {
					minPadding: 0.15,
					maxPadding: 0.15,
					endOnTick: false,
					gridLineWidth: 0,
					title: {
						text: null
					},
					labels: {
						enabled: false
					},
					softMax: 4
				},
				legend: {
					align: 'left',
					verticalAlign: 'top',
					padding: 0
				},
				plotOptions: {
					scatter: {
						stacking: 'normal',
						accessibility: {
							description: this.translate.instant('feedback-graph-description', {markers: this.markerSeriesData.length, comments: this.commentSeriesData.length})
						},
						point: {
							events: {
								click: (e) => {
									this.playerSync.seek(e.point.options.custom.actualTime);
								}
							}
						}
					}
				},
				tooltip: {
					outside: true,
					headerFormat: '',
					formatter: (a) => {
						const point = a.chart.hoverPoint;
						const timePipe = new FormatTimePipe();
						const options = point.options as SeriesData;
						let val = timePipe.transform(options.custom.actualTime);
						if (options.custom.markerName) {
							val += `<br/><b>${options.custom.commenter}:</b> ${options.custom.markerName}`;
						}
						if (options.custom.commentText) {
							val += `<br/><b>${options.custom.commenter}:</b> ${options.custom.commentText}`;
						}
						return val;
					}
				},
				series: [{
					type: 'scatter',
					name: this.translate.instant('chart_title-tags'),
					data: this.markerSeriesData,
					marker: {
						symbol: 'circle',
						fillColor: '#929292'
					}
				},
				{
					type: 'scatter',
					name: this.translate.instant('chart_title-ai_markers'),
					data: this.aiMarkerSeriesData,
					marker: {
						symbol: 'triangle',
						fillColor: '#929292'
					},
					showInLegend: this.featureFlagService.isAvailable('AI_CONFIG') ? true : false
				},
				{
					type: 'scatter',
					name: this.translate.instant('comments'),
					data: this.commentSeriesData,
					marker: {
						symbol: 'square',
						fillColor: '#333333'
					}
				}],
				credits: {
					enabled: false
				}
			};
			this.cdr.detectChanges();
		});
	}
}
